import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Flag, MessageCircle, TrendingUp, Calendar, DollarSign,
  UserCheck, UserX, AlertTriangle, Shield, Clock, CheckCircle,
  XCircle, Eye, ArrowUpRight, ArrowDownRight, Minus, Activity, Star,
  UserPlus, Hand, CalendarCheck, RefreshCw, Circle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-[#111] border border-white/10 rounded-xl p-5 text-left hover:border-white/20 transition-colors w-full`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[#A1A1AA] text-sm">{title}</p>
        <p className="text-3xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-xs text-[#52525B] mt-1">{subtitle}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${
            trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-[#A1A1AA]'
          }`}>
            {trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
            {trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
            {trend === 'neutral' && <Minus className="w-3 h-3" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
  </button>
);

const MiniChart = ({ data, label, color }) => {
  const max = Math.max(...data, 1);
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-16">
        {data.map((value, idx) => (
          <div
            key={idx}
            className="flex-1 rounded-t transition-all"
            style={{ 
              height: `${(value / max) * 100}%`,
              backgroundColor: color,
              minHeight: '2px'
            }}
          />
        ))}
      </div>
      <p className="text-xs text-[#52525B] text-center">{label}</p>
    </div>
  );
};

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');
  const [overview, setOverview] = useState(null);
  const [reportsBreakdown, setReportsBreakdown] = useState(null);
  const [eventsAnalytics, setEventsAnalytics] = useState(null);
  const [userGrowth, setUserGrowth] = useState(null);
  const [moderationStats, setModerationStats] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchActivityFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/activity-feed?limit=15`);
      setActivityFeed(res.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllAnalytics();
    fetchActivityFeed();
  }, [timeRange, fetchActivityFeed]);

  // Auto-refresh activity feed every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchActivityFeed, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActivityFeed]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, reportsRes, eventsRes, growthRes, moderationRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/reports/breakdown`),
        axios.get(`${API}/events/analytics/overview`),
        axios.get(`${API}/analytics/users/growth?days=${timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90}`).catch(() => ({ data: null })),
        axios.get(`${API}/moderation/stats`).catch(() => ({ data: null }))
      ]);
      
      setOverview(overviewRes.data);
      setReportsBreakdown(reportsRes.data);
      setEventsAnalytics(eventsRes.data);
      setUserGrowth(growthRes.data);
      setModerationStats(moderationRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'registration': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'online': return <Circle className="w-4 h-4 text-green-500 fill-green-500" />;
      case 'message': return <MessageCircle className="w-4 h-4 text-[#7000FF]" />;
      case 'tap': return <Hand className="w-4 h-4 text-[#FF2E63]" />;
      case 'event_registration': return <CalendarCheck className="w-4 h-4 text-[#08D9D6]" />;
      case 'report': return <Flag className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-[#A1A1AA]" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'registration': return 'bg-green-500/10';
      case 'online': return 'bg-green-500/10';
      case 'message': return 'bg-[#7000FF]/10';
      case 'tap': return 'bg-[#FF2E63]/10';
      case 'event_registration': return 'bg-[#08D9D6]/10';
      case 'report': return 'bg-red-500/10';
      default: return 'bg-white/5';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const users = overview?.users || {};
  const reports = overview?.reports || {};
  const engagement = overview?.engagement || {};
  const events = eventsAnalytics?.events || {};
  const revenue = eventsAnalytics?.revenue || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-[#A1A1AA]">Platform performance and trends</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-40 bg-[#111] border-white/10">
            <SelectValue placeholder="Time Range" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
            <SelectItem value="quarter">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={users.total || 0}
          subtitle={`${users.active || 0} active`}
          icon={Users}
          color="#08D9D6"
          trend="up"
          trendValue={`+${users.new_this_week || 0} this week`}
          onClick={() => navigate('/admin/users')}
        />
        <StatCard
          title="Online Now"
          value={users.online_now || 0}
          subtitle="Active in last 15 min"
          icon={Activity}
          color="#22c55e"
        />
        <StatCard
          title="Pending Reports"
          value={reports.pending || 0}
          subtitle={`${reports.today || 0} new today`}
          icon={Flag}
          color="#FF2E63"
          onClick={() => navigate('/admin/reports')}
        />
        <StatCard
          title="Total Revenue"
          value={`$${(revenue.total || 0).toFixed(2)}`}
          subtitle={`${events.total || 0} events`}
          icon={DollarSign}
          color="#7000FF"
          onClick={() => navigate('/admin/events')}
        />
      </div>

      {/* User Growth & Engagement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Card */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-[#08D9D6]" />
              <span className="text-white font-semibold">User Growth</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center mb-6">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-white">{users.new_today || 0}</p>
              <p className="text-xs text-[#A1A1AA]">Today</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-white">{users.new_this_week || 0}</p>
              <p className="text-xs text-[#A1A1AA]">This Week</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-white">{users.new_this_month || 0}</p>
              <p className="text-xs text-[#A1A1AA]">This Month</p>
            </div>
          </div>

          {/* Growth Chart Placeholder */}
          <MiniChart 
            data={userGrowth?.daily_signups || [3, 5, 2, 8, 4, 6, 7]} 
            label="Daily signups (last 7 days)"
            color="#08D9D6"
          />
        </div>

        {/* Engagement Stats */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle className="w-5 h-5 text-[#7000FF]" />
            <span className="text-white font-semibold">Engagement</span>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#7000FF]" />
                <span className="text-[#A1A1AA]">Messages Today</span>
              </div>
              <span className="text-xl font-bold text-white">{engagement.messages_today || 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#7000FF]" />
                <span className="text-[#A1A1AA]">Messages This Week</span>
              </div>
              <span className="text-xl font-bold text-white">{engagement.messages_this_week || 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="text-[#A1A1AA]">Verified Users</span>
              </div>
              <span className="text-xl font-bold text-white">{users.verified || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Moderation Overview */}
      <div className="bg-[#111] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#FF2E63]" />
            <span className="text-white font-semibold">Moderation Overview</span>
          </div>
          <Button 
            variant="ghost" 
            className="text-[#FF2E63]"
            onClick={() => navigate('/admin/reports')}
          >
            View All Reports
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-[#A1A1AA]">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500">{reports.pending || 0}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-[#A1A1AA]">Resolved</span>
            </div>
            <p className="text-2xl font-bold text-green-500">{reportsBreakdown?.by_status?.resolved || 0}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-[#A1A1AA]">Suspended</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500">{users.suspended || 0}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-[#A1A1AA]">Banned</span>
            </div>
            <p className="text-2xl font-bold text-red-500">{users.banned || 0}</p>
          </div>
        </div>

        {/* Top Report Reasons */}
        {reportsBreakdown?.top_reasons?.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-[#A1A1AA] mb-3">Top Report Reasons</h3>
            <div className="space-y-2">
              {reportsBreakdown.top_reasons.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-white text-sm truncate max-w-[70%]">{item.reason}</span>
                  <span className="text-[#A1A1AA] font-medium">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Events Analytics */}
      <div className="bg-[#111] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-[#7000FF]" />
            <span className="text-white font-semibold">Events Overview</span>
          </div>
          <Button 
            variant="ghost" 
            className="text-[#7000FF]"
            onClick={() => navigate('/admin/events')}
          >
            Manage Events
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-white">{events.total || 0}</p>
            <p className="text-xs text-[#A1A1AA]">Total Events</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-[#22c55e]">{events.upcoming || 0}</p>
            <p className="text-xs text-[#A1A1AA]">Upcoming</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-white">{eventsAnalytics?.registrations?.total || 0}</p>
            <p className="text-xs text-[#A1A1AA]">Registrations</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-[#7000FF]">${(revenue.total || 0).toFixed(2)}</p>
            <p className="text-xs text-[#A1A1AA]">Revenue</p>
          </div>
        </div>

        {/* Events by Category */}
        {eventsAnalytics?.by_category && Object.keys(eventsAnalytics.by_category).length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-[#A1A1AA] mb-3">Events by Category</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(eventsAnalytics.by_category).map(([category, count]) => (
                <span 
                  key={category}
                  className="px-3 py-1 rounded-full bg-[#7000FF]/10 text-[#7000FF] text-sm capitalize"
                >
                  {category}: {count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Real-Time Activity Feed */}
      <div className="bg-[#111] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-[#08D9D6]" />
            <span className="text-white font-semibold">Live Activity Feed</span>
            {autoRefresh && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 text-green-500 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchActivityFeed}
              disabled={feedLoading}
              className="text-[#A1A1AA] hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${feedLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'text-green-500' : 'text-[#A1A1AA]'}
            >
              {autoRefresh ? 'Pause' : 'Resume'}
            </Button>
          </div>
        </div>

        {activityFeed.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No recent activity</p>
            <p className="text-sm text-[#52525B]">Activity will appear here as users interact with the platform</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {activityFeed.map((activity, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-3 p-3 rounded-lg ${getActivityColor(activity.type)} hover:bg-white/10 transition-colors cursor-pointer`}
                onClick={() => activity.user_id && navigate(`/admin/users/${activity.user_id}`)}
              >
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {activity.photo ? (
                    <img src={activity.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getActivityIcon(activity.type)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{activity.message}</p>
                  <p className="text-xs text-[#52525B]">{formatTimeAgo(activity.timestamp)}</p>
                </div>
                <div className="flex-shrink-0">
                  {getActivityIcon(activity.type)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <UserCheck className="w-5 h-5 text-[#22c55e]" />
            <span className="text-[#A1A1AA] font-medium">Active Users</span>
          </div>
          <p className="text-2xl font-bold text-white">{users.active || 0}</p>
          <p className="text-xs text-[#52525B] mt-1">
            {users.total ? Math.round((users.active / users.total) * 100) : 0}% of total
          </p>
        </div>

        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <span className="text-[#A1A1AA] font-medium">Suspended</span>
          </div>
          <p className="text-2xl font-bold text-white">{users.suspended || 0}</p>
          <p className="text-xs text-[#52525B] mt-1">Temporary restrictions</p>
        </div>

        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <UserX className="w-5 h-5 text-red-500" />
            <span className="text-[#A1A1AA] font-medium">Banned</span>
          </div>
          <p className="text-2xl font-bold text-white">{users.banned || 0}</p>
          <p className="text-xs text-[#52525B] mt-1">Permanent bans</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
